/**
 * Checkbooks Management Page
 * 
 * Dedicated page for managing all checkbooks across all accounts.
 * Features:
 * - List all checkbooks with their associated account
 * - Create new checkbooks (select account)
 * - Edit checkbooks
 * - View operation count per checkbook
 * - Link to operations filtered by checkbook
 */

import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Card } from '../components/ui';
import { checkbookService } from '../services/checkbook.service';
import { useAccountStore } from '../store/accountStore';
import { useAuthStore } from '../store/authStore';
import type { Checkbook, CreateCheckbookRequest, UpdateCheckbookRequest } from '@budgira/shared';

interface CheckbookWithInfo extends Checkbook {
  accountName: string;
  bankName: string;
  operationCount: number;
}

interface CheckbookFormData {
  name: string;
  prefix: string;
  startNumber: string;
  nextNumber: string;
  totalChecks: string;
  isActive: boolean;
  accountId: string;
}

const CheckbooksPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { accounts, fetchAccounts } = useAccountStore();
  const { canManageCheckbooks } = useAuthStore();
  const canEdit = canManageCheckbooks();
  
  const [checkbooks, setCheckbooks] = useState<CheckbookWithInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Form state
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingCheckbook, setEditingCheckbook] = useState<CheckbookWithInfo | null>(null);
  const [formData, setFormData] = useState<CheckbookFormData>({
    name: '',
    prefix: '',
    startNumber: '1',
    nextNumber: '1',
    totalChecks: '25',
    isActive: true,
    accountId: '',
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  
  // Delete confirmation
  const [deletingCheckbook, setDeletingCheckbook] = useState<CheckbookWithInfo | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  
  // Filter state
  const [filterAccountId, setFilterAccountId] = useState<string>('');

  const fetchCheckbooks = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await checkbookService.listAll();
      setCheckbooks(data);
    } catch (err: any) {
      setError(err.message || t('checkbooks.errors.generic'));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchCheckbooks();
    fetchAccounts();
  }, [fetchCheckbooks, fetchAccounts]);

  const resetForm = () => {
    setFormData({
      name: '',
      prefix: '',
      startNumber: '1',
      nextNumber: '1',
      totalChecks: '25',
      isActive: true,
      accountId: accounts[0]?.id || '',
    });
    setFormError(null);
    setEditingCheckbook(null);
  };

  const openCreateForm = () => {
    resetForm();
    setIsFormOpen(true);
  };

  const openEditForm = (checkbook: CheckbookWithInfo) => {
    setEditingCheckbook(checkbook);
    setFormData({
      name: checkbook.name,
      prefix: checkbook.prefix || '',
      startNumber: checkbook.startNumber?.toString() || '1',
      nextNumber: checkbook.nextNumber.toString(),
      totalChecks: checkbook.totalChecks?.toString() || '25',
      isActive: checkbook.isActive,
      accountId: checkbook.accountId,
    });
    setFormError(null);
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
    resetForm();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    // Validation
    if (!formData.name.trim()) {
      setFormError(t('checkbooks.form.errors.nameRequired'));
      return;
    }

    if (!editingCheckbook && !formData.accountId) {
      setFormError(t('checkbooks.form.errors.accountRequired', 'Veuillez sélectionner un compte'));
      return;
    }

    const number = editingCheckbook 
      ? parseInt(formData.nextNumber, 10)
      : parseInt(formData.startNumber, 10);
    
    if (isNaN(number) || number < 1) {
      setFormError(t('checkbooks.form.errors.invalidNumber'));
      return;
    }

    const totalChecks = parseInt(formData.totalChecks, 10);
    if (isNaN(totalChecks) || totalChecks < 1) {
      setFormError(t('checkbooks.form.errors.invalidTotalChecks'));
      return;
    }

    try {
      setIsSaving(true);

      if (editingCheckbook) {
        // Update existing checkbook
        const updateData: UpdateCheckbookRequest = {
          name: formData.name.trim(),
          prefix: formData.prefix.trim() || undefined,
          nextNumber: number,
          totalChecks,
          isActive: formData.isActive,
        };
        await checkbookService.update(editingCheckbook.id, updateData);
      } else {
        // Create new checkbook
        const createData: CreateCheckbookRequest = {
          name: formData.name.trim(),
          prefix: formData.prefix.trim() || undefined,
          startNumber: number,
          totalChecks,
          isActive: formData.isActive,
        };
        await checkbookService.create(formData.accountId, createData);
      }

      await fetchCheckbooks();
      closeForm();
    } catch (err: any) {
      setFormError(err.message || t('checkbooks.errors.generic'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingCheckbook) return;

    try {
      setIsDeleting(true);
      setDeleteError(null);
      await checkbookService.delete(deletingCheckbook.id);
      await fetchCheckbooks();
      setDeletingCheckbook(null);
    } catch (err: any) {
      setDeleteError(err.message || t('checkbooks.errors.generic'));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleViewOperations = (checkbook: CheckbookWithInfo) => {
    // Navigate to operations page with filter for this checkbook
    navigate(`/accounts/${checkbook.accountId}/operations?checkbookId=${checkbook.id}`);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">
            {t('checkbooks.pageTitle', 'Chéquiers')}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {t('checkbooks.pageSubtitle', 'Gérez tous vos chéquiers et la numérotation automatique')}
          </p>
        </div>
        {canEdit && (
          <Button onClick={openCreateForm} variant="primary">
            {t('checkbooks.createNew')}
          </Button>
        )}
      </div>

      {/* Filter by account */}
      {accounts.length > 1 && (
        <div className="flex items-center gap-3">
          <label className="text-sm text-slate-600 dark:text-slate-400">
            {t('checkbooks.filterByAccount', 'Filtrer par compte')}:
          </label>
          <select
            value={filterAccountId}
            onChange={(e) => setFilterAccountId(e.target.value)}
            className="px-3 py-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500"
          >
            <option value="">{t('checkbooks.allAccounts', 'Tous les comptes')}</option>
            {accounts.map((account) => (
              <option key={account.id} value={account.id}>
                {account.name} {account.bankName && `- ${account.bankName}`}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Checkbook list */}
      {checkbooks.length === 0 ? (
        <Card className="p-8 text-center">
          <div className="text-slate-400 dark:text-slate-500 mb-4">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <p className="text-slate-500 dark:text-slate-400 mb-4">
            {t('checkbooks.noCheckbooks')}
          </p>
          {canEdit && (
            <Button onClick={openCreateForm} variant="primary">
              {t('checkbooks.createNew')}
            </Button>
          )}
        </Card>
      ) : (
        <div className="grid gap-4">
          {checkbooks
            .filter(cb => !filterAccountId || cb.accountId === filterAccountId)
            .map((checkbook) => {
            // Calculate remaining checks
            const usedChecks = checkbook.nextNumber - (checkbook.startNumber ?? 1);
            const remainingChecks = Math.max(0, (checkbook.totalChecks ?? 25) - usedChecks);
            const isLowOnChecks = remainingChecks <= 5 && remainingChecks > 0;
            const isOutOfChecks = remainingChecks === 0;
            const hasOperations = checkbook.operationCount > 0;
            
            return (
            <Card key={checkbook.id} className="p-4">
              {/* Desktop layout */}
              <div className="hidden sm:flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="font-medium text-slate-900 dark:text-white">
                      {checkbook.name}
                    </h3>
                    {checkbook.prefix && (
                      <span className="px-2 py-0.5 text-xs rounded-full bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400">
                        {t('checkbooks.prefix')}: {checkbook.prefix}
                      </span>
                    )}
                    <span className={`px-2 py-0.5 text-xs rounded-full ${
                      checkbook.isActive 
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400'
                    }`}>
                      {checkbook.isActive ? t('checkbooks.active') : t('checkbooks.inactive')}
                    </span>
                    {isOutOfChecks && (
                      <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                        {t('checkbooks.outOfChecks')}
                      </span>
                    )}
                    {isLowOnChecks && (
                      <span className="px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                        {t('checkbooks.lowOnChecks')}
                      </span>
                    )}
                  </div>
                  
                  {/* Account info */}
                  <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                    📁 {checkbook.accountName} {checkbook.bankName && `- ${checkbook.bankName}`}
                  </p>
                  
                  <div className="flex flex-wrap gap-4 mt-2 text-sm text-slate-500 dark:text-slate-400">
                    <span>
                      {t('checkbooks.nextNumber')}: <strong className="text-slate-700 dark:text-slate-300">{checkbook.prefix || ''}{checkbook.nextNumber}</strong>
                    </span>
                    <span>
                      {t('checkbooks.totalChecks')}: <strong className="text-slate-700 dark:text-slate-300">{checkbook.totalChecks ?? 25}</strong>
                    </span>
                    <span className={isOutOfChecks ? 'text-red-600 dark:text-red-400' : isLowOnChecks ? 'text-amber-600 dark:text-amber-400' : ''}>
                      {t('checkbooks.remainingChecks')}: <strong>{remainingChecks}</strong>
                    </span>
                    <span>
                      {t('checkbooks.operationCount', 'Opérations')}: <strong className="text-slate-700 dark:text-slate-300">{checkbook.operationCount}</strong>
                    </span>
                  </div>
                </div>
                
                <div className="flex gap-2 flex-shrink-0">
                  {hasOperations && (
                    <Button 
                      onClick={() => handleViewOperations(checkbook)} 
                      variant="secondary"
                      size="sm"
                      title={t('checkbooks.viewOperations', 'Voir les opérations')}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                    </Button>
                  )}
                  {canEdit && (
                    <>
                      <Button 
                        onClick={() => openEditForm(checkbook)} 
                        variant="secondary"
                        size="sm"
                      >
                        {t('common.edit')}
                      </Button>
                      <Button 
                        onClick={() => {
                          setDeleteError(null);
                          setDeletingCheckbook(checkbook);
                        }} 
                        variant="danger"
                        size="sm"
                        disabled={hasOperations}
                        title={hasOperations ? t('checkbooks.cannotDeleteUsed', 'Impossible de supprimer un chéquier utilisé') : undefined}
                      >
                        {t('common.delete')}
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {/* Mobile layout */}
              <div className="sm:hidden space-y-3">
                {/* Header with name and badges */}
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-medium text-slate-900 dark:text-white">
                    {checkbook.name}
                  </h3>
                  <div className="flex flex-wrap gap-1 justify-end">
                    <span className={`px-2 py-0.5 text-xs rounded-full whitespace-nowrap ${
                      checkbook.isActive 
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400'
                    }`}>
                      {checkbook.isActive ? t('checkbooks.active') : t('checkbooks.inactive')}
                    </span>
                    {isOutOfChecks && (
                      <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 whitespace-nowrap">
                        {t('checkbooks.outOfChecks')}
                      </span>
                    )}
                    {isLowOnChecks && (
                      <span className="px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 whitespace-nowrap">
                        {t('checkbooks.lowOnChecks')}
                      </span>
                    )}
                  </div>
                </div>

                {/* Account info */}
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  📁 {checkbook.accountName} {checkbook.bankName && `- ${checkbook.bankName}`}
                </p>

                {/* Stats grid */}
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="bg-slate-50 dark:bg-slate-900/50 rounded-lg p-2">
                    <div className="text-slate-500 dark:text-slate-400 text-xs">{t('checkbooks.nextNumber')}</div>
                    <div className="font-medium text-slate-900 dark:text-white">{checkbook.prefix || ''}{checkbook.nextNumber}</div>
                  </div>
                  <div className="bg-slate-50 dark:bg-slate-900/50 rounded-lg p-2">
                    <div className="text-slate-500 dark:text-slate-400 text-xs">{t('checkbooks.totalChecks')}</div>
                    <div className="font-medium text-slate-900 dark:text-white">{checkbook.totalChecks ?? 25}</div>
                  </div>
                  <div className={`rounded-lg p-2 ${isOutOfChecks ? 'bg-red-50 dark:bg-red-900/20' : isLowOnChecks ? 'bg-amber-50 dark:bg-amber-900/20' : 'bg-slate-50 dark:bg-slate-900/50'}`}>
                    <div className={`text-xs ${isOutOfChecks ? 'text-red-600 dark:text-red-400' : isLowOnChecks ? 'text-amber-600 dark:text-amber-400' : 'text-slate-500 dark:text-slate-400'}`}>
                      {t('checkbooks.remainingChecks')}
                    </div>
                    <div className={`font-medium ${isOutOfChecks ? 'text-red-700 dark:text-red-300' : isLowOnChecks ? 'text-amber-700 dark:text-amber-300' : 'text-slate-900 dark:text-white'}`}>
                      {remainingChecks}
                    </div>
                  </div>
                  <div className="bg-slate-50 dark:bg-slate-900/50 rounded-lg p-2">
                    <div className="text-slate-500 dark:text-slate-400 text-xs">{t('checkbooks.operationCount', 'Opérations')}</div>
                    <div className="font-medium text-slate-900 dark:text-white">{checkbook.operationCount}</div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-2 border-t border-slate-100 dark:border-slate-700">
                  {hasOperations && (
                    <Button 
                      onClick={() => handleViewOperations(checkbook)} 
                      variant="secondary"
                      size="sm"
                      className="flex-1"
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                      {t('checkbooks.operations', 'Opérations')}
                    </Button>
                  )}
                  {canEdit && (
                    <>
                      <Button 
                        onClick={() => openEditForm(checkbook)} 
                        variant="secondary"
                        size="sm"
                        className={hasOperations ? '' : 'flex-1'}
                      >
                        {t('common.edit')}
                      </Button>
                      <Button 
                        onClick={() => {
                          setDeleteError(null);
                          setDeletingCheckbook(checkbook);
                        }} 
                        variant="danger"
                        size="sm"
                        disabled={hasOperations}
                      >
                        {t('common.delete')}
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </Card>
          )})}
        </div>
      )}

      {/* Create/Edit Form Modal */}
      {isFormOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <form onSubmit={handleSubmit}>
              <div className="p-6">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
                  {editingCheckbook ? t('checkbooks.form.titleEdit') : t('checkbooks.form.titleNew')}
                </h3>

                {formError && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-4">
                    <p className="text-red-700 dark:text-red-400 text-sm">{formError}</p>
                  </div>
                )}

                <div className="space-y-4">
                  {/* Account selector (only for new checkbooks) */}
                  {!editingCheckbook && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                        {t('checkbooks.form.account', 'Compte')} *
                      </label>
                      <select
                        value={formData.accountId}
                        onChange={(e) => setFormData(prev => ({ ...prev, accountId: e.target.value }))}
                        className="w-full px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-colors"
                        required
                      >
                        <option value="">{t('checkbooks.form.selectAccount', 'Sélectionner un compte')}</option>
                        {accounts.map((account) => (
                          <option key={account.id} value={account.id}>
                            {account.name} {account.bankName && `- ${account.bankName}`}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Show account info when editing */}
                  {editingCheckbook && (
                    <div className="bg-slate-50 dark:bg-slate-900/50 rounded-lg p-3">
                      <p className="text-sm text-slate-600 dark:text-slate-400">
                        <span className="font-medium">{t('checkbooks.form.account', 'Compte')}:</span>{' '}
                        {editingCheckbook.accountName} {editingCheckbook.bankName && `- ${editingCheckbook.bankName}`}
                      </p>
                    </div>
                  )}

                  {/* Name */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      {t('checkbooks.form.name')} *
                    </label>
                    <Input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder={t('checkbooks.form.namePlaceholder')}
                      required
                    />
                  </div>

                  {/* Prefix */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      {t('checkbooks.form.prefix')}
                    </label>
                    <Input
                      type="text"
                      value={formData.prefix}
                      onChange={(e) => setFormData(prev => ({ ...prev, prefix: e.target.value }))}
                      placeholder={t('checkbooks.form.prefixPlaceholder')}
                    />
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                      {t('checkbooks.form.prefixHelp')}
                    </p>
                  </div>

                  {/* Start Number (create) or Next Number (edit) */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      {editingCheckbook ? t('checkbooks.form.nextNumber') : t('checkbooks.form.startNumber')} *
                    </label>
                    <Input
                      type="number"
                      min="1"
                      value={editingCheckbook ? formData.nextNumber : formData.startNumber}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        [editingCheckbook ? 'nextNumber' : 'startNumber']: e.target.value 
                      }))}
                      required
                    />
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                      {editingCheckbook ? t('checkbooks.form.nextNumberHelp') : t('checkbooks.form.startNumberHelp')}
                    </p>
                    {!editingCheckbook && (
                      <p className="text-xs text-amber-600 dark:text-amber-400 mt-2 bg-amber-50 dark:bg-amber-900/20 p-2 rounded">
                        💡 {t('checkbooks.form.resumeHint', 'Pour un chéquier déjà entamé, indiquez le numéro du prochain chèque disponible.')}
                      </p>
                    )}
                  </div>

                  {/* Total Checks */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      {t('checkbooks.form.totalChecks')} *
                    </label>
                    <Input
                      type="number"
                      min="1"
                      value={formData.totalChecks}
                      onChange={(e) => setFormData(prev => ({ ...prev, totalChecks: e.target.value }))}
                      required
                    />
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                      {t('checkbooks.form.totalChecksHelp')}
                    </p>
                  </div>

                  {/* Is Active */}
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="isActive"
                      checked={formData.isActive}
                      onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                      className="w-4 h-4 text-violet-600 bg-white dark:bg-slate-700 border-slate-300 dark:border-slate-600 rounded focus:ring-violet-500"
                    />
                    <label htmlFor="isActive" className="text-sm text-slate-700 dark:text-slate-300">
                      {t('checkbooks.form.isActive')}
                    </label>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 -mt-2 ml-7">
                    {t('checkbooks.form.isActiveHelp')}
                  </p>
                </div>
              </div>

              <div className="flex justify-end gap-3 px-6 py-4 bg-slate-50 dark:bg-slate-900/50 rounded-b-xl">
                <Button type="button" onClick={closeForm} variant="secondary">
                  {t('common.cancel')}
                </Button>
                <Button type="submit" variant="primary" disabled={isSaving}>
                  {isSaving ? t('common.saving') : (editingCheckbook ? t('common.save') : t('checkbooks.form.create'))}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingCheckbook && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
              {t('checkbooks.deleteConfirm.title')}
            </h3>
            <p className="text-slate-600 dark:text-slate-400 mb-2">
              {t('checkbooks.deleteConfirm.message', { name: deletingCheckbook.name })}
            </p>
            {deletingCheckbook.operationCount > 0 && (
              <p className="text-sm text-red-600 dark:text-red-400 mb-4 bg-red-50 dark:bg-red-900/20 p-2 rounded">
                ⚠️ {t('checkbooks.deleteConfirm.hasOperations', {
                  count: deletingCheckbook.operationCount,
                  defaultValue: `Ce chéquier est utilisé par ${deletingCheckbook.operationCount} opération(s) et ne peut pas être supprimé.`
                })}
              </p>
            )}
            {deleteError && (
              <p className="text-sm text-red-600 dark:text-red-400 mb-4">
                {deleteError}
              </p>
            )}
            <p className="text-sm text-slate-500 dark:text-slate-500 mb-6">
              {t('checkbooks.deleteConfirm.warning')}
            </p>
            <div className="flex justify-end gap-3">
              <Button 
                onClick={() => setDeletingCheckbook(null)} 
                variant="secondary"
                disabled={isDeleting}
              >
                {t('common.cancel')}
              </Button>
              <Button 
                onClick={handleDelete} 
                variant="danger"
                disabled={isDeleting || deletingCheckbook.operationCount > 0}
              >
                {isDeleting ? t('common.deleting') : t('common.delete')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckbooksPage;
